import { useEffect, useState } from 'react';

import { AppIcon, UploadedFile, ConfirmDelete } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { profileApi, StoryDocument } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { checkUserDocuments } from '@backrs_ui/grow-ui/src/redux/user/thunks/userDocument.thunk';

import { Box, Theme, SxProps, Tooltip } from '@mui/material';

import { ProfileFormDocument } from '../ProfileFormDocument';
import { FileUploadConfig } from '../types/profile.types';
import { handleDeleteStory } from '../utils/deleteStory.util';

const DELETE_STRINGS = appStrings.modules.profile.edit;
const LINK_STRINGS = appStrings.modules.profile.modalUploader.document.link;

const MEDIA_TYPE_TO_FILE_EXTENSION: Record<StoryDocument['media_type'], string> = {
  text: 'txt',
  txt: 'txt',
  document: 'pdf',
  video: 'mp4',
  pdf: 'pdf',
  docx: 'docx',
  image: 'png',
  link: 'txt',
};

type ProfileDocumentViewProps = Readonly<{
  fileUploadConfig: FileUploadConfig;
  storyDocuments: StoryDocument[];
}>;

export const ProfileDocumentView = ({
  fileUploadConfig,
  storyDocuments,
}: ProfileDocumentViewProps) => {
  const [documents, setDocuments] = useState(storyDocuments);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<StoryDocument | null>(null);
  const isIntroVideo = fileUploadConfig.type === 'purpose_video';

  const dispatch = useAppDispatch();

  const handleDeleteClick = (document: StoryDocument) => {
    setSelectedDocument(document);
    setOpenDialog(true);
  };

  const handleDeleteSuccess = (deletedId: number) => {
    setDocuments((prev) => prev.filter((doc) => doc.id !== deletedId));

    // now invalidate the user profile
    dispatch(profileApi.util.invalidateTags(['UserProfile']));

    dispatch(checkUserDocuments());
  };

  useEffect(() => {
    setDocuments(storyDocuments);
  }, [storyDocuments]);

  return (
    <Box>
      <ConfirmDelete
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        onSuccess={handleDeleteSuccess}
        headerText={DELETE_STRINGS.deleteDocumentConfirmHeader(isIntroVideo)}
        bodyText={DELETE_STRINGS.deleteDocumentConfirmBody(isIntroVideo)}
        handleDelete={() =>
          handleDeleteStory({
            selectedDocument,
            onSuccess: handleDeleteSuccess,
            setOpenDialog,
            setError: () => null,
          })
        }
      />

      {(!fileUploadConfig.overrideModal || documents.length === 0) && (
        <ProfileFormDocument
          fileUploadConfig={fileUploadConfig}
          onError={(error) => {
            console.error('Error uploading document:', error);
          }}
        />
      )}
      <Box sx={documentsContainerSx}>
        {documents.map((document) => {
          if (document.source_url) {
            const isUnreadable = document.viewable === 'false';

            return (
              <Box
                key={document.id}
                id={`profile-document-link-${document.id}`}
                sx={documentContainerSx}
                onClick={() => window.open(document.source_url, '_blank', 'noopener,noreferrer')}
              >
                <UploadedFile
                  fileName={document.title || document.source_url}
                  fileSize={document.description || ''}
                  fileType="link"
                  endAdornment={
                    isUnreadable ? (
                      <Tooltip title={LINK_STRINGS.unreadableTooltip}>
                        <Box sx={linkIconSx}>
                          <AppIcon
                            iconName="AlertTriangle"
                            diameter="16px"
                            color="Colors/Text/text-warning-primary"
                          />
                        </Box>
                      </Tooltip>
                    ) : undefined
                  }
                  handleDelete={() => handleDeleteClick(document)}
                />
              </Box>
            );
          }

          const isPurposeVideo = document.question_id === 'video';

          // override to force mp4 if it's a purpose video
          const fileType = isPurposeVideo
            ? 'mp4'
            : MEDIA_TYPE_TO_FILE_EXTENSION[document.media_type];

          const fileName = isPurposeVideo ? 'My Intro Video' : document.title || '';
          const fileSize = isPurposeVideo
            ? new Date(document.updated_at || '').getFullYear().toString()
            : document.description || '';

          return (
            <Box
              key={document.id}
              id={`profile-document-${document.id}`}
              sx={documentContainerSx}
              // NOTE: WHAT SHOULD BEHAVIOR BE FORE PROFILE VIDEO?
              onClick={() => window.open(document.media_url, '_blank', 'noopener,noreferrer')}
            >
              <UploadedFile
                fileName={fileName}
                fileSize={fileSize}
                fileType={fileType}
                handleDelete={() => handleDeleteClick(document)}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const documentsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  mt: backrs.spacing['spacing-lg'],
});

const documentContainerSx: SxProps<Theme> = {
  cursor: 'pointer',
};

const linkIconSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-md'],
  paddingTop: backrs.spacing['spacing-none'],
  paddingRight: backrs.spacing['spacing-none'],
});
